import { useEffect, useState } from 'react';
import { createLogger } from '@/lib/logger';
import { draftStore } from '@/lib/draftStore';
import { View, ActivityIndicator, Platform, UIManager } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Tabs, useRouter } from 'expo-router';
import { Activity, Trophy, Swords, Plus, User, Building2 } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { isFounderSystemVisible } from '@/lib/founder/isFounderSystemVisible';
import { devTierOverride } from '@/lib/devTierOverride';
import { TYPO } from '@/constants/typography';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { didJustCompleteOnboarding, clearOnboardingJustCompleted } from '@/lib/onboardingFlag';
import { useUnreadNotificationCount } from '@/hooks/useUnreadNotificationCount';


const log = createLogger('_LAYOUT');

// Enable LayoutAnimation on Android
if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

export default function TabLayout() {
  const router = useRouter();
  const { theme } = useTheme();
  const { session, profile, loading } = useAuth();
  const insets = useSafeAreaInsets();

  const [localOnboardingDone, setLocalOnboardingDone] = useState(false);
  const [gateReady, setGateReady] = useState(false);

  // Check local flag on mount (handles race between DB update and profile refresh)
  useEffect(() => {
    AsyncStorage.getItem('onboarding:walkthrough_complete').then((v) => {
      if (v === 'true') setLocalOnboardingDone(true);
    }).catch(() => {}).finally(() => setGateReady(true));
  }, []);

  useEffect(() => {
    // Wait for both auth loading AND local storage check before making gate decisions
    if (loading || !gateReady) return;

    if (!session) {
      router.replace('/(auth)/welcome');
      return;
    }

    // Allow entry if profile says completed, local async flag, or synchronous in-memory flag
    if (!profile?.onboarding_completed && !localOnboardingDone && !didJustCompleteOnboarding()) {
      // On web, give the profile a moment to rehydrate before redirecting
      // This prevents false redirects when Chrome restores a suspended tab
      if (Platform.OS === 'web' && !profile) {
        log.info('[TABS_LAYOUT] Web: profile not loaded yet, waiting before redirect');
        return;
      }
      log.info('[TABS_LAYOUT] Onboarding not complete, redirecting');
      router.replace('/(onboarding)/home-gym');
      return;
    }

    // Clear the in-memory flag once profile catches up
    if (profile?.onboarding_completed && didJustCompleteOnboarding()) {
      clearOnboardingJustCompleted();
    }
  }, [session, profile, loading, localOnboardingDone, gateReady]);

  // Initialize dev tier override
  useEffect(() => {
    void devTierOverride.init();
  }, []);

  // Initialize persistent draft store
  useEffect(() => {
    void draftStore.init();
  }, []);

  const unreadCount = useUnreadNotificationCount();

  // Prevent rendering tabs before auth state is resolved
  if (loading || !session) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: theme.colors.background }}>
        <ActivityIndicator size="large" color={theme.colors.accent} />
      </View>
    );
  }

  return (
    <Tabs
      screenOptions={{
        headerShown: false,
        tabBarStyle: {
          backgroundColor: theme.colors.background,
          borderTopColor: theme.colors.border,
          borderTopWidth: 1,
          height: (Platform.OS === 'ios' ? 56 : 52) + insets.bottom,
          paddingBottom: insets.bottom > 0 ? insets.bottom : 8,
          paddingTop: 10,
        },
        tabBarActiveTintColor: theme.colors.accent,
        tabBarInactiveTintColor: theme.colors.textTertiary,
        tabBarLabelStyle: {
          fontSize: 10,
          fontFamily: TYPO.subheading.bold,
          letterSpacing: 2,
          textTransform: 'uppercase',
        },
      }}>
      <Tabs.Screen
        name="home"
        options={{
          title: 'Home',
          tabBarAccessibilityLabel: 'Home dashboard',
          tabBarIcon: ({ size, color }) => <Activity size={size} color={color} />,
          tabBarBadge: unreadCount > 0 ? unreadCount : undefined,
          tabBarBadgeStyle: { backgroundColor: '#EF4444', fontSize: 10, minWidth: 18, height: 18, lineHeight: 18, borderRadius: 9 },
        }}
      />
      <Tabs.Screen
        name="rank"
        options={{
          title: 'Rank',
          tabBarAccessibilityLabel: 'Leaderboard rankings',
          tabBarIcon: ({ size, color }) => <Trophy size={size} color={color} />,
        }}
      />
      <Tabs.Screen
        name="record"
        options={{
          title: 'Log',
          tabBarAccessibilityLabel: 'Record a workout',
          tabBarIcon: ({ size, color }) => (
            <Plus size={size + 8} color={color} strokeWidth={3} />
          ),
        }}
      />
      <Tabs.Screen
        name="duels"
        options={{
          title: 'Duel',
          tabBarAccessibilityLabel: 'Duels and challenges',
          tabBarIcon: ({ size, color }) => <Swords size={size} color={color} />,
        }}
      />
      <Tabs.Screen
        name="profile"
        options={{
          title: 'Profile',
          tabBarAccessibilityLabel: 'Your profile',
          tabBarIcon: ({ size, color }) => <User size={size} color={color} />,
        }}
      />
      <Tabs.Screen
        name="founder-leaderboard"
        options={isFounderSystemVisible({ email: session?.user?.email }) ? {
          title: 'Founders',
          tabBarAccessibilityLabel: 'Founder leaderboard',
          tabBarIcon: ({ size, color }) => <Building2 size={size} color={color} />,
        } : { href: null }}
      />
      <Tabs.Screen name="feed" options={{ href: null }} />
      <Tabs.Screen name="leaderboards" options={{ href: null }} />{/* rank.tsx re-exports this; kept for expo-router */}
      <Tabs.Screen name="map" options={{ href: null }} />
      <Tabs.Screen name="edit-profile" options={{ href: null }} />
      <Tabs.Screen name="badges" options={{ href: null }} />
      <Tabs.Screen name="friends" options={{ href: null }} />
      <Tabs.Screen name="body-metrics" options={{ href: null }} />
      <Tabs.Screen name="notifications" options={{ href: null }} />
      <Tabs.Screen name="verify" options={{ href: null }} />
    </Tabs>
  );
}
